import {
  COMPLIANCE_SUMMARY,
  ENTITY_PROFILE_DETAIL,
  ENTITY_STATUS_SUMMARY,
  QUESTION_RESPONSE_SUMMARY,
  QUESTION_STATISTICS_SUMMARY,
} from "./report_generation_builder_constants";
import {
  get_allowed_date_dimension_names,
  is_question_scope_report_type,
} from "./report_generation_builder_helpers";

/**
 * Computed properties that drive the report builder UI.
 */
const get_date_value = (from_value, to_value) => {
  if (from_value && to_value) {
    return `${from_value} to ${to_value}`;
  }
  if (from_value) {
    return `From ${from_value}`;
  }
  if (to_value) {
    return `Until ${to_value}`;
  }
  return "No date filter";
};

const get_question_value = (component) => {
  if (!component.is_question_scope_report) {
    return `${component.selected_field_ids.length} summary columns`;
  }
  if (component.selected_questions.length) {
    return `${component.selected_questions.length} selected`;
  }
  if (component.is_question_statistics_summary) {
    return component.translate_label("Question selection required");
  }
  return component.translate_label("All scoped questions");
};

const get_question_description = (component) => {
  if (!component.is_question_scope_report) {
    return component.non_question_scope_summary_description;
  }
  if (component.selected_questions.length) {
    return component.selected_questions_description;
  }
  return component.empty_selected_questions_description;
};

export default {
  output_formats() {
    return Reflect.get(this.lookup_data, "output_formats") || [];
  },
  entity_types() {
    return Reflect.get(this.lookup_data, "entity_types") || [];
  },
  date_dimensions() {
    return Reflect.get(this.lookup_data, "date_dimensions") || [];
  },
  report_metric_types() {
    return Reflect.get(this.lookup_data, "report_metric_types") || [];
  },
  report_types() {
    const report_types = Reflect.get(this.lookup_data, "report_types") || [];
    if (!this.is_profile_scope) {
      return report_types;
    }
    return report_types.filter(
      (report_type) => report_type.internal_name === ENTITY_PROFILE_DETAIL
    );
  },
  selected_report_type() {
    const selected_report_type_id = this.normalize_id(this.selected_report_type_id);
    return this.report_types.find(
      (report_type) => report_type.id === selected_report_type_id
    );
  },
  target_entity_profile_id() {
    const parsed = Number(this.initial_target_entity_profile_id || 0);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  },
  initial_entity_type_id_value() {
    const parsed = Number(this.initial_entity_type_id || 0);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  },
  is_profile_scope() {
    return Boolean(this.target_entity_profile_id);
  },
  selected_report_type_internal_name() {
    return this.selected_report_type?.internal_name || ENTITY_PROFILE_DETAIL;
  },
  selected_report_type_label() {
    return this.translate_label(
      this.selected_report_type?.display_name || "Entity Profile Detail"
    );
  },
  selected_output_format_label() {
    const output_format = this.output_formats.find(
      (item) => item.id === this.normalize_id(this.selected_output_format_id)
    );
    return this.translate_label(output_format?.display_name || "Output format");
  },
  is_question_scope_report() {
    return is_question_scope_report_type(this.selected_report_type_internal_name);
  },
  template_entity_type_ids() {
    if (this.is_profile_scope && this.initial_entity_type_id_value) {
      return [this.initial_entity_type_id_value];
    }
    return this.use_project_scope ? [] : this.selected_entity_type_ids;
  },
  template_scope_internal_name() {
    return this.template_entity_type_ids.length
      ? "selected_entity_types"
      : "project_wide";
  },
  available_report_fields() {
    return this.get_report_fields(this.selected_report_type_internal_name);
  },
  allowed_date_dimensions() {
    const allowed_names = get_allowed_date_dimension_names(
      this.selected_report_type_internal_name
    );
    return this.date_dimensions.filter((date_dimension) =>
      allowed_names.includes(date_dimension.internal_name)
    );
  },
  has_available_report_fields() {
    return this.available_report_fields.length > 0;
  },
  filtered_questions() {
    const search_term = this.question_search.toLowerCase().trim();
    if (!search_term) {
      return this.question_list;
    }
    return this.question_list.filter((question) => {
      const search_text = `${question.question_text} ${question.question_type}`;
      return search_text.toLowerCase().includes(search_term);
    });
  },
  preview_columns() {
    return this.available_report_fields.filter((field) =>
      this.selected_field_ids.includes(field.id)
    );
  },
  is_editing_template() {
    return Boolean(this.editing_template_id);
  },
  builder_title() {
    if (this.selected_report_type_internal_name === COMPLIANCE_SUMMARY) {
      return this.translate_label("Compliance summary reports");
    }
    if (this.selected_report_type_internal_name === ENTITY_STATUS_SUMMARY) {
      return this.translate_label("Entity status summary reports");
    }
    if (this.selected_report_type_internal_name === QUESTION_RESPONSE_SUMMARY) {
      return this.translate_label("Question response summary reports");
    }
    if (this.selected_report_type_internal_name === QUESTION_STATISTICS_SUMMARY) {
      return this.translate_label("Question statistics summary reports");
    }
    return this.translate_label("Entity profile reports");
  },
  builder_description() {
    if (this.selected_report_type_internal_name === COMPLIANCE_SUMMARY) {
      return this.translate_label(
        "Build a compliance-focused report using the selected compliance columns for the current entity scope."
      );
    }
    if (this.selected_report_type_internal_name === ENTITY_STATUS_SUMMARY) {
      return this.translate_label(
        "Build an aggregated report that counts entities by the summary dimensions you select."
      );
    }
    if (this.selected_report_type_internal_name === QUESTION_RESPONSE_SUMMARY) {
      return this.translate_label(
        "Build an aggregated report that shows how many entities answered a question, how many selected specific answers, and how many repeatable answer occurrences were captured."
      );
    }
    if (this.selected_report_type_internal_name === QUESTION_STATISTICS_SUMMARY) {
      return this.translate_label(
        "Build an aggregated report that calculates per-question statistics such as answered counts, repeatable-answer occurrence counts, numeric totals, averages, and most selected answers."
      );
    }
    return this.translate_label(
      "Build an Excel-style report where each entity appears once and every selected question becomes its own column."
    );
  },
  layout_preview_text() {
    if (this.selected_report_type_internal_name === COMPLIANCE_SUMMARY) {
      return "1 row = 1 compliance evaluation result";
    }
    if (this.selected_report_type_internal_name === ENTITY_STATUS_SUMMARY) {
      return "1 row = 1 entity type and status combination";
    }
    if (this.selected_report_type_internal_name === QUESTION_RESPONSE_SUMMARY) {
      return "1 row = 1 question summary or answer-option summary";
    }
    if (this.selected_report_type_internal_name === QUESTION_STATISTICS_SUMMARY) {
      return "1 row = 1 question statistic result";
    }
    return "1 row = 1 entity profile";
  },
  missing_report_fields_message() {
    if (this.selected_report_type_internal_name === COMPLIANCE_SUMMARY) {
      return "No compliance columns are available for this report type right now. Refresh the page or rerun the report lookup population command if the problem persists.";
    }
    if (this.selected_report_type_internal_name === ENTITY_STATUS_SUMMARY) {
      return "No summary columns are available for this report type right now. Refresh the page or rerun the report lookup population command if the problem persists.";
    }
    if (this.selected_report_type_internal_name === QUESTION_RESPONSE_SUMMARY) {
      return "No question-summary columns are available for this report type right now. Refresh the page or rerun the report lookup population command if the problem persists.";
    }
    if (this.selected_report_type_internal_name === QUESTION_STATISTICS_SUMMARY) {
      return "No statistic-summary columns are available for this report type right now. Refresh the page or rerun the report lookup population command if the problem persists.";
    }
    return "No columns are available for this report type right now. Refresh the page or rerun the report lookup population command if the problem persists.";
  },
  date_dimension_help_text() {
    if (this.selected_report_type_internal_name === ENTITY_STATUS_SUMMARY) {
      return this.translate_label(
        "Choose whether the range should operate on when the entity onboarded or when the application moved through a status transition."
      );
    }
    if (this.selected_report_type_internal_name === QUESTION_STATISTICS_SUMMARY) {
      return this.translate_label(
        "Choose whether the range should operate on when the entity onboarded or when the answer used for the statistic was submitted."
      );
    }
    return this.translate_label(
      "Choose whether the range should operate on when the entity onboarded or when the answer was submitted."
    );
  },
  question_scope_description() {
    if (this.selected_report_type_internal_name === QUESTION_RESPONSE_SUMMARY) {
      return this.translate_label(
        "Drill down by form, main section, and sub-section so you only choose the questions that should be summarized."
      );
    }
    if (this.selected_report_type_internal_name === QUESTION_STATISTICS_SUMMARY) {
      return this.translate_label(
        "Drill down by form, main section, and sub-section so you only choose the questions that should be included in the statistics summary."
      );
    }
    return this.translate_label(
      "Drill down by form, main section, and sub-section so you only choose the questions that should become report columns."
    );
  },
  selected_questions_description() {
    if (this.selected_report_type_internal_name === QUESTION_RESPONSE_SUMMARY) {
      return this.translate_label(
        "Each selected question becomes one or more summary rows. Add optional answer filters for select-type questions here."
      );
    }
    if (this.selected_report_type_internal_name === QUESTION_STATISTICS_SUMMARY) {
      return this.translate_label(
        "Each selected question becomes one or more statistic rows. Choose at least one metric per question and add optional answer filters for select-type questions when needed."
      );
    }
    return this.translate_label(
      "Each selected question becomes an answer column. Add optional answer filters for select-type questions here."
    );
  },
  empty_selected_questions_title() {
    if (this.selected_report_type_internal_name === QUESTION_RESPONSE_SUMMARY) {
      return this.translate_label("All scoped questions will be summarized");
    }
    if (this.selected_report_type_internal_name === QUESTION_STATISTICS_SUMMARY) {
      return this.translate_label("Choose questions to calculate statistics");
    }
    return this.translate_label("All scoped questions will be included");
  },
  empty_selected_questions_description() {
    if (this.selected_report_type_internal_name === QUESTION_RESPONSE_SUMMARY) {
      return this.translate_label(
        "Leave this list empty to summarize every question available for the selected project or entity types."
      );
    }
    if (this.selected_report_type_internal_name === QUESTION_STATISTICS_SUMMARY) {
      return this.translate_label(
        "Question statistics require explicit question selection so the correct metrics can be configured for each question."
      );
    }
    return this.translate_label(
      "Leave this list empty to include every question available for the selected project or entity types."
    );
  },
  non_question_scope_summary_description() {
    if (this.selected_report_type_internal_name === COMPLIANCE_SUMMARY) {
      return this.translate_label(
        "This report returns one row per compliance result in the selected scope."
      );
    }
    return this.translate_label(
      "This report groups entities by the selected summary dimensions and returns counts for each resulting combination."
    );
  },
  required_field_id() {
    if (this.selected_report_type_internal_name !== ENTITY_PROFILE_DETAIL) {
      return null;
    }
    return this.get_lookup_id("report_fields", "entity_name");
  },
  is_question_statistics_summary() {
    return this.selected_report_type_internal_name === QUESTION_STATISTICS_SUMMARY;
  },
  template_mode_value() {
    return this.save_as_template
      ? this.translate_label("Reusable template")
      : this.translate_label("One-off run");
  },
  template_mode_description() {
    if (this.is_editing_template) {
      return this.translate_label("This saved template will be updated before generation.");
    }
    return this.save_as_template
      ? this.translate_label("This setup will be available in the template library.")
      : this.translate_label("A hidden history template will be stored only for traceability.");
  },
  scope_summary_value() {
    if (this.is_profile_scope) {
      return this.initial_entity_name || this.translate_label("Single entity");
    }
    if (this.use_project_scope) {
      return this.translate_label("All entity types");
    }
    return this.translate_label(`${this.selected_entity_type_ids.length} selected`);
  },
  scope_summary_description() {
    if (this.is_profile_scope) {
      return this.translate_label(
        "Question scope stays aligned to the selected profile's entity type."
      );
    }
    if (this.use_project_scope) {
      return this.translate_label("The report can include every entity type in this project.");
    }
    return this.translate_label("Only the selected entity types will be included.");
  },
  date_summary_value() {
    return this.translate_label(
      get_date_value(this.selected_date_from, this.selected_date_to)
    );
  },
  date_summary_description() {
    const date_dimension = this.allowed_date_dimensions.find(
      (item) => item.id === this.normalize_id(this.selected_date_dimension_id)
    );
    return this.translate_label(date_dimension?.display_name || "No date dimension selected");
  },
  question_setup_value() {
    return get_question_value(this);
  },
  question_setup_description() {
    return get_question_description(this);
  },
  builder_summary_cards() {
    return [
      {
        label: this.translate_label("Template mode"),
        value: this.template_mode_value,
        description: this.template_mode_description,
      },
      {
        label: this.translate_label("Output"),
        value: this.selected_output_format_label,
        description: this.layout_preview_text,
      },
      {
        label: this.translate_label("Scope"),
        value: this.scope_summary_value,
        description: this.scope_summary_description,
      },
      {
        label: this.translate_label("Date range"),
        value: this.date_summary_value,
        description: this.date_summary_description,
      },
      {
        label: this.translate_label("Questions and columns"),
        value: this.question_setup_value,
        description: this.question_setup_description,
      },
    ];
  },
};
